import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { ChatDockService, DockWindow } from '../../chat-dock.service';
import { ChatThreadComponent } from '../chat-thread/chat-thread.component';
import { ConversationService } from '../../../service/core/ConversationService.service';
import { convInitials, convSubtitle, convTitle } from '../../conversation-display.util';

/**
 * The floating chat dock: a launcher pinned bottom-right on every screen, a conversation-list panel
 * and pop-out chat windows that stack along the bottom edge.
 *
 * <p>Each window renders the SAME {@link ChatThreadComponent} the Live Conversations page uses, so
 * the dock is not a cut-down chat: media, the 24-hour window with its template picker, voice notes
 * and delivery ticks all behave identically, and a fix lands in both places at once.</p>
 *
 * <p>The launcher owns the bottom-right corner. State lives in {@link ChatDockService}
 * (root-provided) so open chats survive navigation — this component is only a renderer, and it is
 * mounted from {@code src/app.component.ts}, the real shell.</p>
 */
@Component({
  selector: 'app-chat-dock',
  standalone: true,
  imports: [
    CommonModule, FormsModule, InputTextModule, IconFieldModule, InputIconModule,
    TooltipModule, ToastModule, ChatThreadComponent
  ],
  templateUrl: './chat-dock.component.html',
  styleUrls: ['./chat-dock.component.scss'],
  /*
   * ChatThreadComponent does inject(MessageService), and PrimeNG's MessageService is NOT
   * providedIn:'root' - the Live Conversations page supplies its own (conversation.ts,
   * `providers: [MessageService, ConfirmationService]`). The dock is mounted in app.component.ts,
   * the ROOT shell, where nothing provided it, so constructing the thread threw
   * NullInjectorError and the window body rendered EMPTY while this component's own header still
   * drew fine. That is a runtime-only failure: `ng build` and `tsc` both pass, which is why three
   * rounds of CSS fixes never touched it.
   *
   * Provided here rather than at the root so the instance is paired with the <p-toast> in this
   * template - the same pairing the inbox uses. A root MessageService with no root toast would
   * swallow every send failure silently instead.
   */
  providers: [MessageService]
})
export class ChatDockComponent implements OnInit, OnDestroy {

  dock = inject(ChatDockService);
  private conversationService = inject(ConversationService);
  private messageService = inject(MessageService);

  convTitle = convTitle;
  convSubtitle = convSubtitle;
  convInitials = convInitials;

  /** Conversations shown in the list panel. Loaded when the panel opens, not on every page. */
  conversations: any[] = [];
  loading = false;
  search = '';
  private searchDebounce: any = null;

  /** Narrow viewports get the full inbox page instead: a 360px panel plus a 320px window would
   *  cover a phone screen entirely. */
  narrow = false;

  ngOnInit(): void {
    this.onResize();
    window.addEventListener('resize', this.onResize);
    this.dock.startBadgePoll();
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.dock.stopBadgePoll();
  }

  private onResize = (): void => {
    const wasNarrow = this.narrow;
    this.narrow = window.innerWidth < 900;
    if (this.narrow && !wasNarrow) this.dock.closeAll();
  };

  get show(): boolean {
    return this.dock.visible && !this.narrow;
  }

  toggleList(): void {
    this.dock.toggleList();
    if (this.dock.listOpen && !this.conversations.length) this.load();
  }

  onSearchChange(): void {
    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.load(), 350);
  }

  /** One page of conversations. Deliberately no background polling: the badge covers "something
   *  arrived", and refreshing a list nobody is looking at is load for every agent on every page. */
  load(): void {
    this.loading = true;
    this.conversationService.getConversations(30, undefined, undefined, { q: this.search })
      .subscribe({
        next: (data: any) => {
          const rows: any[] = Array.isArray(data) ? data : (data?.data ?? []);
          this.conversations = rows.map(r => ({
            waId: r.waId, name: r.name ?? null, contactPhone: r.contactPhone ?? null,
            channel: r.channel ?? null, flowName: r.flowName, handoff: r.handoffStatus,
            unreadCount: r.unreadCount ?? 0, lastMessageAt: r.lastMessageAt ?? null,
            lastMessageText: r.lastMessageText ?? null
          }));
          this.loading = false;
        },
        error: () => { this.conversations = []; this.loading = false; }
      });
  }

  /** waId -> in-flight, so a double tap cannot fire two flips at the same conversation. */
  handoffBusy: Record<string, boolean> = {};

  /* The dock owns this rather than the thread because in compact mode the thread has no header to
   * put it in. Same endpoint, same optimistic write-back onto the row the list is rendering. */
  toggleHandoff(w: DockWindow): void {
    const waId = w.waId;
    if (!waId || this.handoffBusy[waId]) return;
    const activate = w.conv.handoff !== 'ACTIVE';
    this.handoffBusy[waId] = true;
    this.conversationService.setHandoff(waId, activate).subscribe({
      next: () => {
        this.handoffBusy[waId] = false;
        w.conv.handoff = activate ? 'ACTIVE' : null;
        const row = this.conversations.find(c => c.waId === waId);
        if (row) row.handoff = w.conv.handoff;
        this.messageService.add({
          severity: 'success',
          summary: activate ? 'Handed over to agent' : 'Handed back to AI',
          detail: activate ? 'The AI will stop replying — this chat is now with the team.'
                           : 'The AI will handle this conversation again.'
        });
      },
      error: (err: any) => {
        this.handoffBusy[waId] = false;
        this.messageService.add({
          severity: 'error', summary: 'Hand over failed',
          detail: err?.error?.message || err?.message || 'Could not update the conversation.'
        });
      }
    });
  }

  /** Clock for today, weekday inside the last week, date beyond it — the usual chat-list rule. */
  shortTime(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const days = (now.getTime() - d.getTime()) / 86400000;
    if (days < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
  }

  openChat(conv: any): void {
    if (!this.dock.openChat(conv)) return;   // at the window cap
    this.dock.listOpen = false;
  }

  // ── new chat (restricted to the agent's own assigned cases) ────────────
  newChatOpen = false;
  newChatQuery = '';
  newChatResults: any[] = [];
  newChatSearching = false;
  startingChat = false;
  private newChatDebounce: any = null;

  toggleNewChat(): void {
    this.newChatOpen = !this.newChatOpen;
    if (!this.newChatOpen) { this.newChatQuery = ''; this.newChatResults = []; }
  }

  onNewChatQueryChange(): void {
    clearTimeout(this.newChatDebounce);
    const q = this.newChatQuery.trim();
    if (q.length < 2) { this.newChatResults = []; this.newChatSearching = false; return; }
    this.newChatSearching = true;
    this.newChatDebounce = setTimeout(() => {
      // assignedOnly - the dock only ever offers customers whose ACTIVE case is assigned to me.
      this.conversationService.searchCustomers(q, true).subscribe({
        next: (res: any) => {
          this.newChatResults = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
          this.newChatSearching = false;
        },
        error: () => { this.newChatResults = []; this.newChatSearching = false; }
      });
    }, 300);
  }

  /** Open (or reuse) the chat for this customer's number and pop it out as a window. */
  startChatWith(r: any): void {
    if (this.startingChat || this.dock.atWindowLimit) return;
    this.startingChat = true;
    this.conversationService.startConversation(r.phone || r.waId).subscribe({
      next: (res: any) => {
        const waId = res?.data?.waId ?? res?.waId ?? r.waId;
        this.dock.openChat({ waId, name: r.name ?? null, contactPhone: null, channel: 'WHATSAPP' });
        this.startingChat = false;
        this.toggleNewChat();
        this.dock.listOpen = false;
      },
      error: () => { this.startingChat = false; }
    });
  }

  trackWin = (_: number, w: DockWindow) => w.waId;
  trackConv = (_: number, c: any) => c.waId;
}
